// FormComponents.js
import React from 'react';
import { ProgressBar, Tabs, Tab, Button, Card } from 'react-bootstrap';
import {
    FaCheckCircle,
    FaExclamationCircle,
    FaSave,
    FaUser,
    FaIdCard,
    FaBriefcase,
    FaAddressBook,
    FaLaptop,
    FaBook,
    FaHeartbeat
} from 'react-icons/fa';

// Custom styles
const styles = {
    progressContainer: {
        background: '#f8f9fa',
        borderRadius: '10px',
        padding: '2rem',
        boxShadow: '0 0 15px rgba(0,0,0,0.05)'
    },
    progressBar: {
        height: '1.5rem',
        borderRadius: '8px',
        transition: 'all 0.3s ease'
    },
    progressTitle: {
        color: '#2c3e50',
        fontSize: '1.5rem',
        marginBottom: '1.5rem'
    },
    tabContainer: {
        background: '#fff',
        borderRadius: '10px',
        padding: '1rem',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
    },
    tab: {
        margin: '0.5rem 0',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        padding: '0.8rem 1rem'
    },
    tabActive: {
        background: '#4285f4',
        color: '#fff'
    },
    saveButton: {
        borderRadius: '8px',
        padding: '1rem',
        fontWeight: '600',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }
};

/**
 * Improved Progress Bar Component
 */
export const FormProgress = ({ totalProgress, sectionProgress }) => (
    <div style={styles.progressContainer}>
        <h4 className="fw-bold text-center" style={styles.progressTitle}>
            Form Completion Progress
        </h4>
        <ProgressBar
            now={totalProgress}
            label={`${totalProgress}%`}
            variant={totalProgress === 100 ? "success" : "primary"}
            className="mb-3 shadow-sm"
            style={styles.progressBar}
        />
        <div className="text-center">
            <small className="text-muted">
                {totalProgress === 100 ? (
                    <span className="text-success">
                        <FaCheckCircle className="me-1" /> All sections complete!
                    </span>
                ) : (
                    `${100 - totalProgress}% remaining to complete`
                )}
            </small>
        </div>
    </div>
);

/**
 * Enhanced Tab Title Component
 */
const TabTitle = ({ section, sectionProgress, Icon, label }) => {
    const isComplete = Math.abs((sectionProgress[section] || 0) - 1) < 0.001;

    return (
        <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
                <Icon className="me-2" />
                <span>{label}</span>
            </div>
            <div className="d-flex align-items-center">
                {isComplete ? (
                    <div className="d-flex align-items-center text-success">
                        <FaCheckCircle className="ms-2" />
                        <small className="ms-1">Complete</small>
                    </div>
                ) : (
                    <div className="d-flex align-items-center text-warning">
                        <FaExclamationCircle className="ms-2" />
                        <small className="ms-1">
                            {Math.round((sectionProgress[section] || 0) * 100)}%
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Improved Navigation Tabs
 */
export const FormTabs = ({ activeTab, setActiveTab, sectionProgress }) => (
    <Card style={styles.tabContainer}>
        <Card.Body className="p-2">
            <Tabs
                activeKey={activeTab}
                onSelect={setActiveTab}
                className="flex-column nav-pills border-0"
                transition={false}
            >
                {[
                    { key: 'staff_info', Icon: FaUser, label: 'Staff Information' },
                    { key: 'identification_doc', Icon: FaIdCard, label: 'Identification' },
                    { key: 'employment', Icon: FaBriefcase, label: 'Employment' },
                    { key: 'contact', Icon: FaAddressBook, label: 'Contact' },
                    { key: 'equipment', Icon: FaLaptop, label: 'Equipment' },
                    { key: 'training', Icon: FaBook, label: 'Training' },
                    { key: 'medical', Icon: FaHeartbeat, label: 'Medical' }
                ].map(({ key, Icon, label }) => (
                    <Tab
                        key={key}
                        eventKey={key}
                        title={
                            <TabTitle
                                section={key}
                                sectionProgress={sectionProgress}
                                Icon={Icon}
                                label={label}
                            />
                        }
                        tabClassName={`${styles.tab} ${activeTab === key ? 'active' : ''}`}
                    />
                ))}
            </Tabs>
        </Card.Body>
    </Card>
);

/**
 * Enhanced Save Button
 */
export const SaveButton = ({ isComplete, loading, onSave }) => (
    <Card className="mt-4 border-0">
        <Card.Body className="p-2">
            <Button
                variant={isComplete ? "success" : "secondary"}
                size="lg"
                onClick={onSave}
                disabled={!isComplete || loading}
                className="w-100"
                style={{
                    ...styles.saveButton,
                    opacity: (!isComplete || loading) ? 0.7 : 1
                }}
            >
                {loading ? (
                    'Saving...'
                ) : (
                    <>
                        <FaSave className="me-2" />
                        {isComplete ? 'Save All Changes' : 'Complete Required Fields'}
                    </>
                )}
            </Button>
        </Card.Body>
    </Card>
);